/**
 * The two peer connections an SOS call needs, and why it is two and not one.
 *
 * The operator RECEIVES the wearer's audio and video, and SENDS their microphone. Those cannot ride
 * one connection here: the receive side plays a stream the media server transcoded, and the send
 * side publishes a stream of its own that the helmet is then told to pull. They are two different
 * streams on the media server, so they are two exchanges and two peers.
 *
 * Both are NON-TRICKLE. The broker's exchange is a single request/response — offer in, answer out —
 * with nowhere to deliver a late candidate, so the offer must be complete before it is sent. That
 * is what `waitForIceGathering` is for, and dropping it produces a call that signals perfectly and
 * carries no media.
 */

import { CALL_ICE_SERVERS, exchangeCallSdp } from './helmet-call-signal'

/** ICE gathering is normally sub-second on a LAN; this only bounds a pathological case. */
const ICE_GATHER_TIMEOUT_MS = 3_000

/**
 * Resolve once the offer is complete.
 *
 * Resolves on TIMEOUT as well as completion, on purpose: a partially gathered offer that contains
 * the host and server-reflexive candidates usually connects, and refusing to send it because one
 * STUN server was slow would fail a call that would otherwise have worked.
 */
export function waitForIceGathering(pc: RTCPeerConnection, timeoutMs = ICE_GATHER_TIMEOUT_MS): Promise<void> {
  if (pc.iceGatheringState === 'complete') return Promise.resolve()

  return new Promise<void>(resolve => {
    let done = false

    const finish = () => {
      if (done) return
      done = true
      clearTimeout(timer)
      pc.removeEventListener('icegatheringstatechange', onChange)
      resolve()
    }

    const onChange = () => {
      if (pc.iceGatheringState === 'complete') finish()
    }

    const timer = setTimeout(finish, timeoutMs)

    pc.addEventListener('icegatheringstatechange', onChange)
  })
}

export interface PeerDeps {
  url: string
  token: string | null
  signal?: AbortSignal
  /** Injected for tests; production passes nothing and gets the real constructor. */
  createPeer?: () => RTCPeerConnection
  fetchImpl?: typeof fetch
  /** Called when the connection drops after having been established. */
  onDropped?: () => void
}

function makePeer(deps: PeerDeps): RTCPeerConnection {
  return deps.createPeer ? deps.createPeer() : new RTCPeerConnection({ iceServers: CALL_ICE_SERVERS })
}

/**
 * Watch for a peer that dies AFTER connecting.
 *
 * Only `failed` and `closed` count. `disconnected` is routinely transient — a Wi-Fi handover raises
 * it and it recovers on its own — and treating it as the end of the call would hang up on an
 * operator walking between access points.
 */
function watchForDrop(pc: RTCPeerConnection, onDropped?: () => void): void {
  if (!onDropped) return
  pc.addEventListener('connectionstatechange', () => {
    if (pc.connectionState === 'failed' || pc.connectionState === 'closed') onDropped()
  })
}

/**
 * Publish the operator's microphone.
 *
 * The track is added before the offer is created, so the offer describes real audio. Publishing
 * must complete BEFORE the helmet is told to join: the vendor's flow has the device PULL this
 * stream, and a device told to pull a stream that is not there fails once and gives up.
 */
export async function openMicPeer(
  track: MediaStreamTrack,
  stream: MediaStream,
  deps: PeerDeps,
): Promise<RTCPeerConnection> {
  const pc = makePeer(deps)

  try {
    pc.addTrack(track, stream)

    const offer = await pc.createOffer()

    await pc.setLocalDescription(offer)
    await waitForIceGathering(pc)

    const answer = await exchangeCallSdp({
      url: deps.url,
      offer: pc.localDescription?.sdp ?? offer.sdp ?? '',
      token: deps.token,
      ...(deps.signal ? { signal: deps.signal } : {}),
      ...(deps.fetchImpl ? { fetchImpl: deps.fetchImpl } : {}),
    })

    await pc.setRemoteDescription({ type: 'answer', sdp: answer })
    watchForDrop(pc, deps.onDropped)

    return pc
  } catch (err) {
    // Close on ANY failure. A half-open peer holds the microphone, which the operator sees as a
    // live mic indicator on a call that never started.
    pc.close()
    throw err
  }
}

/**
 * Receive the wearer's side.
 *
 * Receive-only transceivers rather than waiting for tracks to be offered: the answer comes from the
 * media server, and without declaring what we want to receive there is nothing for it to answer.
 * Both media kinds are declared even though the audio is what matters — the helmet's camera is part
 * of an emergency call, and a video-less offer would refuse it silently.
 */
export async function openPlayPeer(
  deps: PeerDeps & { onTrack: (stream: MediaStream) => void },
): Promise<RTCPeerConnection> {
  const pc = makePeer(deps)

  try {
    pc.addEventListener('track', ev => {
      const [remote] = ev.streams

      deps.onTrack(remote ?? new MediaStream([ev.track]))
    })
    pc.addTransceiver('audio', { direction: 'recvonly' })
    pc.addTransceiver('video', { direction: 'recvonly' })

    const offer = await pc.createOffer()

    await pc.setLocalDescription(offer)
    await waitForIceGathering(pc)

    const answer = await exchangeCallSdp({
      url: deps.url,
      offer: pc.localDescription?.sdp ?? offer.sdp ?? '',
      token: deps.token,
      ...(deps.signal ? { signal: deps.signal } : {}),
      ...(deps.fetchImpl ? { fetchImpl: deps.fetchImpl } : {}),
    })

    await pc.setRemoteDescription({ type: 'answer', sdp: answer })
    watchForDrop(pc, deps.onDropped)

    return pc
  } catch (err) {
    // A fresh peer per attempt, so the failed one must go: an offer can be answered exactly once,
    // and retrying on the same peer produces an InvalidStateError rather than a second attempt.
    pc.close()
    throw err
  }
}
